import React, { useState, useRef, useEffect } from "react";
import { ReviewHeader } from "./ReviewHeader";
import { ReviewSidebar } from "./ReviewSidebar";
import { ReviewContent } from "./ReviewContent";
import MobileQuestionNav from "./MobileQuestionNav";
import FloatingNavButtons from "../shared/FloatingNavButtons";

export function ReviewPage({
  courseType = "Quick Practice",
  score = 33,
  currentLevel = 4.29,
  questions = [],
  onBack,
  apiResponse = null, // Full API response from SUBMIT API (for accuracy, soals-stats)
  userAnswers = [], // Raw user answers array (for reference)
  langCode = null, // Language code ('de' for German, 'en' for English)
}) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const reviewMainContentRef = useRef(null);

  // Extract soals-stats from apiResponse (from SUBMIT API) for accuracy mapping
  const soalsStats =
    apiResponse?.data?.["to-display"]?.["soals-stats"] ||
    apiResponse?.["to-display"]?.["soals-stats"] ||
    [];

  // Build map soal-id -> stat for reliable mapping
  // CRITICAL: Map berdasarkan soal-id dari SUBMIT API (soals-stats)
  // changed by Cursor: tambah drill-id support untuk vocab drill
  const soalStatsById = {};
  soalsStats.forEach((s) => {
    const id = s?.["soal-id"] || s?.soalId || s?.["drill-id"] || s?.id;
    if (id) {
      soalStatsById[id] = s;
    }
  });

  // Build map proset-id -> user answer from userAnswers array (from SUBMIT payload)
  const userAnswersByProsetId = {};
  const userAnswersBySoalId = {};

  if (Array.isArray(userAnswers)) {
    userAnswers.forEach((ua) => {
      const prosetId = ua?.["proset-id"] || ua?.prosetId || ua?.proset_id;
      // changed by Cursor: tambah ua?.questionId karena writing/speaking practice simpan soal-id di field 'questionId'
      const soalId = ua?.["soal-id"] || ua?.soalId || ua?.soal_id || ua?.questionId;
      let userAnswer =
        ua?.["user-answer"] ?? ua?.userAnswer ?? ua?.answer ?? ua;

      // CRITICAL: Untuk pilgan-multi, pastikan userAnswer tetap dalam format [0, 3] (array of numbers)
      // Jika sudah numbers, langsung pakai. Jika text array, tetap simpan (akan di-handle di MultipleCorrectAnswer.jsx)
      if (
        (ua?.type === "pilgan-multi" || ua?.type === "mc") &&
        Array.isArray(userAnswer)
      ) {
        // Jika semua adalah numbers, langsung pakai
        const allNumbers = userAnswer.every((item) => typeof item === "number");
        if (!allNumbers) {
          // Ada string - log warning tapi tetap simpan (akan di-handle di MultipleCorrectAnswer.jsx)
          console.warn(
            "⚠️ index.jsx - userAnswer contains strings for pilgan-multi:",
            {
              "soal-id": soalId,
              "proset-id": prosetId,
              "userAnswer (text)": userAnswer,
              "ua[user-answer]": ua?.["user-answer"],
            }
          );
        } else {
          // Pastikan semua adalah numbers (convert string numbers jika ada)
          userAnswer = userAnswer.map((id) => {
            if (typeof id === "number") return id;
            if (
              typeof id === "string" &&
              !isNaN(Number(id)) &&
              id.trim() !== ""
            )
              return Number(id);
            return id;
          });
        }
      }

      if (prosetId) {
        userAnswersByProsetId[prosetId] = userAnswer;
      }
      if (soalId) {
        userAnswersBySoalId[soalId] = userAnswer;
      }
    });
  }

  // Filter questions: hanya tampilkan yang ada di soals-stats (yang di-submit)
  // CRITICAL: Jika ada soals-stats, hanya tampilkan questions yang match dengan soals-stats
  // FIX: Handle duplicate entries dari report soal - gunakan unique soal-id untuk filter
  const filteredQuestions =
    soalsStats.length > 0
      ? (() => {
          // Build set of unique soal-ids from soals-stats (handle duplicates from report soal)
          // changed by Cursor: tambah drill-id support untuk vocab drill
          const uniqueSoalIds = new Set();
          soalsStats.forEach((s) => {
            const id = s?.["soal-id"] || s?.soalId || s?.["drill-id"] || s?.id;
            if (id) {
              uniqueSoalIds.add(id);
            }
          });

          // Track which soal-ids we've already included to prevent duplicates
          const includedSoalIds = new Set();

          // Filter questions: hanya yang ada di unique soal-ids dan belum di-include
          // changed by Cursor: tambah drill-id support untuk vocab drill
          const filtered = questions.filter((question, index) => {
            const originalQ = question?.originalQuestion || question;
            const soalIdFromGet =
              originalQ?.["soal-id"] ||
              originalQ?.soalId ||
              originalQ?.["drill-id"] ||
              originalQ?.id ||
              question?.["soal-id"] ||
              question?.soalId ||
              question?.["drill-id"] ||
              question?.id;

            // Check if this question exists in unique soal-ids and not already included
            if (soalIdFromGet && uniqueSoalIds.has(soalIdFromGet)) {
              // Only include if not already added (prevent duplicates)
              if (!includedSoalIds.has(soalIdFromGet)) {
                includedSoalIds.add(soalIdFromGet);
                return true;
              }
              return false;
            }

            // Fallback: check by index if soal-id doesn't match (untuk handle soal tanpa soal-id)
            // Hanya jika index masih dalam range unique soal-ids count dan belum di-include
            if (
              index < uniqueSoalIds.size &&
              !includedSoalIds.has(`index-${index}`)
            ) {
              includedSoalIds.add(`index-${index}`);
              return true;
            }

            // Exclude if not in soals-stats or already included
            return false;
          });

          // Limit to original questions count (not soals-stats count which may have duplicates)
          return filtered.slice(0, questions.length);
        })()
      : questions; // If no soals-stats, show all questions

  // Enhance filtered questions with accuracy from soals-stats (SUBMIT API)
  // Match soal-id dari GET API (originalQuestion) dengan soal-id dari SUBMIT API (soals-stats)
  const enhancedQuestions = filteredQuestions.map((question, index) => {
    const originalQ = question?.originalQuestion || question;

    // Get IDs dari GET API (originalQuestion)
    // changed by Cursor: tambah drill-id support untuk vocab drill
    const prosetIdFromGet =
      originalQ?.["proset-id"] || originalQ?.prosetId || originalQ?.proset_id;
    const soalIdFromGet =
      originalQ?.["soal-id"] ||
      originalQ?.soalId ||
      originalQ?.["drill-id"] ||
      originalQ?.id ||
      question?.["soal-id"] ||
      question?.soalId ||
      question?.["drill-id"] ||
      question?.id;

    // Match dengan soals-stats berdasarkan soal-id untuk accuracy
    let matchedSoalStat = soalIdFromGet ? soalStatsById[soalIdFromGet] : null;

    // FALLBACK: Jika tidak match berdasarkan soal-id, gunakan index-based mapping
    // Setelah filter, index sudah sesuai dengan soals-stats karena hanya questions yang ada di soals-stats yang ditampilkan
    if (!matchedSoalStat && soalsStats[index]) {
      matchedSoalStat = soalsStats[index];
      if (process.env.NODE_ENV === "development") {
        console.warn(
          `⚠️ ReviewPage - soal-id tidak match untuk question ${
            index + 1
          }, menggunakan index-based mapping`,
          {
            soalIdFromGet,
            matchedSoalStatSoalId:
              matchedSoalStat?.["soal-id"] ||
              matchedSoalStat?.soalId ||
              matchedSoalStat?.id,
            accuracy: matchedSoalStat?.accuracy,
          }
        );
      }
    }

    // Get accuracy dari matched soals-stats
    // Check apakah accuracy dari backend sudah dalam range 0-1 atau 0-100
    let accuracy;
    if (matchedSoalStat?.accuracy !== undefined) {
      const rawAccuracy = matchedSoalStat.accuracy;
      // Jika accuracy > 1, berarti dalam range 0-100, convert ke 0-1
      // Jika accuracy <= 1, berarti sudah dalam range 0-1
      accuracy = rawAccuracy > 1 ? rawAccuracy / 100 : rawAccuracy;
    } else {
      // Fallback: gunakan question.accuracy atau isCorrect
      accuracy =
        question.accuracy !== undefined
          ? question.accuracy > 1
            ? question.accuracy / 100
            : question.accuracy
          : question.isCorrect
          ? 1.0
          : 0.0;
    }

    // CRITICAL: Correct answer SELALU dari GET API (originalQuestion.answers)
    const correctAnswerFromGet =
      originalQ?.answers ||
      originalQ?.correctAnswer ||
      originalQ?.["correct-answer"] ||
      question.correctAnswer ||
      [];

    // CRITICAL: User answer dicocokkan berdasarkan proset-id atau soal-id dari userAnswers array
    // JANGAN pakai question.userAnswer karena mungkin sudah text array
    let matchedUserAnswer = null;

    // changed by Cursor: untuk speaking/pronunciation, prefer question.userAnswer dari ResultPage
    // karena ResultPage sudah format dengan audioUrl dari localStorage.
    // Raw userAnswers map cuma punya text string (ua.userAnswer), bukan object {audioUrl, text}.
    const questionType = (originalQ?.type || question?.type || '').toLowerCase();
    if (
      (questionType === 'speaking' || questionType === 'pronunciation' || questionType === 'pronun') &&
      question.userAnswer &&
      typeof question.userAnswer === 'object' &&
      !Array.isArray(question.userAnswer)
    ) {
      // Keep the rich object from ResultPage (has audioUrl + text/assessment)
      matchedUserAnswer = question.userAnswer;
    } else {
      // CRITICAL: Match dengan proset-id DULU, baru soal-id
      // Gunakan !== undefined untuk check karena 0 atau [] adalah falsy tapi valid
      if (
        prosetIdFromGet &&
        userAnswersByProsetId.hasOwnProperty(prosetIdFromGet)
      ) {
        matchedUserAnswer = userAnswersByProsetId[prosetIdFromGet];
      } else if (
        soalIdFromGet &&
        userAnswersBySoalId.hasOwnProperty(soalIdFromGet)
      ) {
        matchedUserAnswer = userAnswersBySoalId[soalIdFromGet];
      }

      // Fallback ke question.userAnswer dari ResultPage jika tidak ada match di userAnswers map
      // Ini penting untuk writing yang sudah di-format di ResultPage
      if (matchedUserAnswer === null || matchedUserAnswer === undefined) {
        matchedUserAnswer = question.userAnswer !== undefined && question.userAnswer !== null
          ? question.userAnswer
          : [];
      }
    }

    return {
      ...question,
      // Accuracy from SUBMIT API (soals-stats[].accuracy) - convert 0-100 to 0-1
      accuracy: accuracy,
      // CRITICAL: Correct answer SELALU dari GET API (bukan dari SUBMIT)
      correctAnswer: correctAnswerFromGet,
      // CRITICAL: User answer dicocokkan berdasarkan proset-id atau soal-id
      userAnswer: matchedUserAnswer,
      // Store matched soal stat for reference
      matchedSoalStat: matchedSoalStat,
      // changed by Cursor: tambah questionIndex untuk speaking/pronunciation localStorage fallback
      // (questionNumber di review harus sesuai index, bukan hardcoded 1)
      questionIndex: index,
    };
  });

  // Get current question from enhanced questions
  const currentQuestion = enhancedQuestions[currentQuestionIndex];
  const totalQuestions = enhancedQuestions.length;

  // Enhance question with accuracy from SUBMIT API
  const enhancedQuestion = currentQuestion;

  // changed by Cursor: auto scroll ke atas setiap ganti soal (via useEffect on currentQuestionIndex)
  useEffect(() => {
    // Scroll window ke paling atas setiap kali currentQuestionIndex berubah
    // Gunakan multiple fallback supaya works di semua browser (termasuk mobile Safari/Chrome)
    window.scrollTo({ top: 0, behavior: "smooth" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    // Juga scroll container .review-main-content ke atas jika punya scrollbar sendiri
    if (reviewMainContentRef.current) {
      reviewMainContentRef.current.scrollTop = 0;
    }
  }, [currentQuestionIndex]);

  const handleQuestionSelect = (index) => {
    setCurrentQuestionIndex(index);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (reviewMainContentRef.current) {
      reviewMainContentRef.current.scrollTop = 0;
    }
  };

  const hasPrevious = currentQuestionIndex > 0;
  const hasNext = currentQuestionIndex < totalQuestions - 1;

  return (
    <div className="min-h-screen bg-[var(--card-bg)] flex flex-col">
      <ReviewHeader onBack={onBack} />

      <div className="flex-1 flex flex-col gap-4 p-4 px-2 pb-[100px] max-w-[1600px] mx-auto w-full xl:flex-row xl:gap-5 xl:p-5 xl:px-10 xl:pb-5">
        <ReviewSidebar
          score={score}
          totalQuestions={totalQuestions}
          currentLevel={currentLevel}
          questions={enhancedQuestions}
          currentQuestionIndex={currentQuestionIndex}
          onQuestionSelect={handleQuestionSelect}
          soalsStats={soalsStats}
          soalStatsById={soalStatsById}
        />

        <div className="review-main-content flex-1 flex flex-col gap-3 min-w-0" ref={reviewMainContentRef}>
          <ReviewContent 
            question={enhancedQuestion} 
            courseType={courseType}
            langCode={langCode || apiResponse?.data?.['lang-code'] || apiResponse?.['lang-code'] || null}
            currentQuestionNumber={currentQuestionIndex + 1}
            totalQuestions={totalQuestions}
          />
        </div>
      </div>

      {/* Fixed nav: Prev kiri bawah, Next kanan bawah, Up kanan atas (tepat di atas Next) */}
      {totalQuestions >= 1 && (
        <FloatingNavButtons
          showPrev={totalQuestions > 1}
          showNext={totalQuestions > 1}
          showUp
          prevDisabled={!hasPrevious}
          nextDisabled={!hasNext}
          onPrev={handlePreviousQuestion}
          onNext={handleNextQuestion}
          onUp={scrollToTop}
          prevLabel="Previous question"
          nextLabel="Next question"
          containerClassName="z-[100]"
          accentNext={hasNext}
        />
      )}

      {/* Mobile Question Navigation - Floating Button & Panel */}
      <MobileQuestionNav
        questions={enhancedQuestions}
        currentQuestionIndex={currentQuestionIndex}
        onQuestionSelect={handleQuestionSelect}
      />
    </div>
  );
}

export default ReviewPage;
